import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, 
  Button, 
  Table, 
  Space, 
  Typography, 
  Row, 
  Col,
  message, 
  Modal, 
  Input,
  Select,
  InputNumber,
  Switch,
  Radio,
  Tag,
  Popconfirm,
  Tooltip,
  Form,
  Tabs,
  Spin,
  Progress,
  Alert
} from 'antd';
import { 
  ArrowLeftOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SettingOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { hasRole } from '../utils/permissionUtils';
import { contactImportApi } from '../services/contactImportApi';
import { broadcastGroupApi, hashtagApi } from '../services/contactApi';
import ContactImportFieldMapping from '../components/ContactImportFieldMapping';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ContactImportSchedulePage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('userInfo') || '{}') : {};
  const isTenantAdmin = hasRole(userInfo, 'Tenant_Admin');

  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState('basic');
  const [runningContactScheduleIds, setRunningContactScheduleIds] = useState([]);
  const [contactProgressByScheduleId, setContactProgressByScheduleId] = useState({});
  
  // 字段映射相關狀態
  const [previewData, setPreviewData] = useState([]);
  const [previewColumns, setPreviewColumns] = useState([]);
  const [sqlPreviewMeta, setSqlPreviewMeta] = useState(null);
  const [groups, setGroups] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [fieldMappingForm] = Form.useForm();
  const previewDataLoadedRef = useRef(false);

  const [formData, setFormData] = useState({
    name: '',
    isScheduled: true,
    scheduleType: 'interval',
    intervalMinutes: 60,
    status: 'Active',
    isActive: true,
    importType: 'excel',
    sourceConfig: {},
    fieldMapping: {}
  });

  useEffect(() => {
    loadSchedules();
    loadGroups();
    loadHashtags();
  }, []);

  useEffect(() => {
    if (runningContactScheduleIds.length === 0) return undefined;
    const tick = async () => {
      for (const sid of runningContactScheduleIds) {
        try {
          const exr = await contactImportApi.getScheduleExecutions(sid);
          const list = exr.executions || [];
          const running = list.find((e) => String(e.status || '').toLowerCase() === 'running');
          if (running) {
            const pr = await contactImportApi.getExecutionImportProgress(running.id);
            if (pr?.success && pr.data) {
              setContactProgressByScheduleId((prev) => ({ ...prev, [sid]: pr.data }));
              const st = pr.data.syncStatus;
              if (st === 'Completed' || st === 'Failed') {
                setRunningContactScheduleIds((prev) => prev.filter((x) => x !== sid));
              }
            }
          } else if (list.length > 0) {
            const latest = list[0];
            const latestStatus = String(latest.status || '').toLowerCase();
            if (latestStatus === 'success' || latestStatus === 'failed') {
              const ok = Number(latest.successCount || 0);
              const fail = Number(latest.failedCount || 0);
              const total = Number(latest.totalRecords || 0);
              setContactProgressByScheduleId((prev) => ({
                ...prev,
                [sid]: {
                  syncStatus: latestStatus === 'failed' ? 'Failed' : 'Completed',
                  totalRecordsToSync: total,
                  recordsProcessed: ok + fail,
                  recordsInserted: ok,
                  importFailCount: fail,
                  recordsSkipped: 0,
                  syncErrorMessage: latest.errorMessage || null,
                },
              }));
              setRunningContactScheduleIds((prev) => prev.filter((x) => x !== sid));
            }
          }
        } catch {
          /* ignore */
        }
      }
    };
    tick();
    const iv = setInterval(tick, 3000);
    return () => clearInterval(iv);
  }, [runningContactScheduleIds]);
  
  // 當編輯 modal 打開且 formData 準備好時，自動載入預覽數據
  useEffect(() => {
    if (editModalVisible && editingSchedule && formData.sourceConfig && Object.keys(formData.sourceConfig).length > 0 && !previewDataLoadedRef.current) {
      console.log('📋 useEffect 觸發載入預覽數據:', { formData, editingSchedule });
      previewDataLoadedRef.current = true;
      // 延遲一點確保所有狀態都已設置
      const timer = setTimeout(() => {
        loadPreviewData();
      }, 300);
      return () => clearTimeout(timer);
    }
    
    // 當 modal 關閉時重置標誌
    if (!editModalVisible) {
      previewDataLoadedRef.current = false;
      setPreviewData([]);
      setPreviewColumns([]);
      setSqlPreviewMeta(null);
    }
  }, [editModalVisible, editingSchedule?.id, formData.sourceConfig]);
  
  // 載入廣播群組列表
  const loadGroups = async () => {
    try {
      const groupsResponse = await broadcastGroupApi.getGroups();
      const groupsData = Array.isArray(groupsResponse) ? groupsResponse : 
                        (groupsResponse?.data && Array.isArray(groupsResponse.data)) ? groupsResponse.data : [];
      setGroups(groupsData);
    } catch (error) {
      console.error('載入群組列表失敗:', error);
    }
  };

  // 載入標籤主檔列表
  const loadHashtags = async () => {
    try {
      const hashtagsResponse = await hashtagApi.getHashtags();
      const hashtagsData = Array.isArray(hashtagsResponse) ? hashtagsResponse : 
                          (hashtagsResponse?.data && Array.isArray(hashtagsResponse.data)) ? hashtagsResponse.data : [];
      setHashtags(hashtagsData);
    } catch (error) {
      console.error('載入標籤列表失敗:', error);
    }
  };
  
  // 載入數據預覽（用於字段映射）
  const loadPreviewData = async () => {
    if (!editingSchedule || !formData.sourceConfig) {
      console.log('⚠️ 無法載入預覽數據：缺少 sourceConfig', { editingSchedule, formData });
      return;
    }
    
    // 確保 sourceConfig 是對象
    let sourceConfig = formData.sourceConfig;
    if (typeof sourceConfig === 'string') {
      try {
        sourceConfig = JSON.parse(sourceConfig);
      } catch (e) {
        console.error('❌ 解析 sourceConfig 失敗:', e);
        message.warning('無法解析數據源配置');
        return;
      }
    }
    
    // 檢查必要的配置字段
    const importType = editingSchedule.importType || formData.importType;
    if (importType === 'excel' && !sourceConfig.filePath) {
      console.warn('⚠️ Excel 配置缺少 filePath');
      return;
    }
    if (importType === 'google' && !sourceConfig.url && !sourceConfig.spreadsheetId) {
      console.warn('⚠️ Google Sheets 配置缺少 url 或 spreadsheetId');
      return;
    }
    if (importType === 'sql' && !sourceConfig.server && !sourceConfig.query) {
      console.warn('⚠️ SQL 配置缺少必要字段');
      return;
    }
    if (importType === 'sql' && !isTenantAdmin) {
      return;
    }
    
    try {
      setLoadingPreview(true);
      console.log('🚀 開始載入預覽數據:', { importType, sourceConfig });
      
      let result;
      
      if (importType === 'excel') {
        result = await contactImportApi.loadFromExcel(sourceConfig);
      } else if (importType === 'google') {
        result = await contactImportApi.loadFromGoogleDocs(sourceConfig);
      } else if (importType === 'sql') {
        result = await contactImportApi.loadFromSql({ ...sourceConfig, previewMode: true });
      } else {
        console.warn('⚠️ 未知的匯入類型:', importType);
        return;
      }
      
      console.log('📊 預覽數據載入結果:', result);
      
      if (result && result.success) {
        const data = result.data || [];
        const columns = result.columns || [];
        
        console.log('✅ 成功載入預覽數據:', { 
          dataCount: data.length, 
          columnsCount: columns.length,
          columns: columns 
        });
        
        setPreviewData(data);
        setPreviewColumns(columns.map(col => ({ 
          title: col, 
          dataIndex: col, 
          key: col 
        })));
        if (importType === 'sql') {
          setSqlPreviewMeta({
            totalMatchingRows: result.totalMatchingRows,
            previewTopApplied: result.previewTopApplied,
            previewTopSkippedForWithCte: result.previewTopSkippedForWithCte,
          });
        } else {
          setSqlPreviewMeta(null);
        }
        
        message.success(`成功載入 ${data.length} 筆預覽數據`);
      } else {
        console.error('❌ 載入失敗:', result);
        message.warning(result?.message || '無法載入數據預覽，但可以手動編輯字段映射');
      }
    } catch (error) {
      console.error('❌ 載入預覽數據失敗:', error);
      console.error('❌ 錯誤詳情:', {
        message: error.message,
        response: error.response?.data,
        stack: error.stack
      });
      message.warning('無法載入數據預覽：' + (error.message || '未知錯誤') + '，但可以手動編輯字段映射');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleReloadPreview = async () => {
    await loadPreviewData();
  };

  const handleTestSql = async () => {
    if (!isTenantAdmin) {
      message.error(t('dataSetManagement.tenantAdminOnly', '僅 Tenant_Admin 可用'));
      return;
    }
    let srcCfg = formData.sourceConfig;
    if (typeof srcCfg === 'string') {
      try {
        srcCfg = JSON.parse(srcCfg);
      } catch (e) {
        message.error('無法解析數據源配置');
        return;
      }
    }
    try {
      const result = await contactImportApi.testSqlConnection(srcCfg);
      if (result.success) message.success(`${t('contactImport.testConnection')} OK`);
      else message.error(result.message || t('commerceImport.schedulePreviewFailed'));
    } catch (e) {
      message.error((e.response?.data?.message || e.message) || t('commerceImport.schedulePreviewFailed'));
    }
  };

  // 載入排程列表
  const loadSchedules = async () => {
    try {
      setLoading(true);
      const response = await contactImportApi.getSchedules();
      if (response.success) {
        const nextSchedules = response.schedules || [];
        setSchedules(nextSchedules);
        const runningIds = [];
        await Promise.all(
          nextSchedules.map(async (s) => {
            try {
              const exr = await contactImportApi.getScheduleExecutions(s.id);
              const list = exr.executions || [];
              const running = list.find((e) => String(e.status || '').toLowerCase() === 'running');
              if (running) runningIds.push(s.id);
            } catch {
              /* ignore */
            }
          })
        );
        if (runningIds.length > 0) {
          setRunningContactScheduleIds((prev) => Array.from(new Set([...prev, ...runningIds])));
        }
      }
    } catch (error) {
      message.error('載入排程列表失敗：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 刪除排程
  const handleDelete = async (id) => {
    try {
      await contactImportApi.deleteSchedule(id);
      message.success('排程已刪除');
      loadSchedules();
    } catch (error) {
      message.error('刪除排程失敗：' + error.message);
    }
  };

  // 手動執行排程
  const handleExecute = async (schedule) => {
    let keepRunning = false;
    setRunningContactScheduleIds((a) => (a.includes(schedule.id) ? a : [...a, schedule.id]));
    try {
      message.loading({
        content: t('contactImport.importExecutionProgress') + '…',
        key: 'execute',
        duration: 0,
      });
      const result = await contactImportApi.executeSchedule(schedule.id);
      if (result?.started) {
        message.success({
          content: result.message || '匯入已在背景執行，請查看進度條',
          key: 'execute',
          duration: 5,
        });
        setContactProgressByScheduleId((prev) => ({
          ...prev,
          [schedule.id]: {
            syncStatus: 'Running',
            totalRecordsToSync: 0,
            recordsProcessed: 0,
            recordsInserted: 0,
            importFailCount: 0,
            recordsSkipped: 0,
            syncErrorMessage: null,
          },
        }));
        keepRunning = true;
        loadSchedules();
        return;
      }
      message.success({
        content:
          result.message ||
          `匯入完成：成功 ${result.execution?.successCount || 0} 筆，失敗 ${result.execution?.failedCount || 0} 筆`,
        key: 'execute',
        duration: 5,
      });
      const ex = result.execution;
      if (ex) {
        const ok = Number(ex.successCount ?? 0);
        const fail = Number(ex.failedCount ?? 0);
        const total = Number(ex.totalRecords ?? 0);
        const st = (ex.status || '').toString();
        setContactProgressByScheduleId((prev) => ({
          ...prev,
          [schedule.id]: {
            syncStatus: st === 'Failed' ? 'Failed' : 'Completed',
            totalRecordsToSync: total,
            recordsProcessed: ok + fail,
            recordsInserted: ok,
            importFailCount: fail,
            recordsSkipped: 0,
            syncErrorMessage: ex.errorMessage || null,
          },
        }));
      }
      loadSchedules();
    } catch (error) {
      console.error('執行排程失敗:', error);
      if (error.response?.status === 409) {
        keepRunning = true;
        message.warning({
          content: error.response?.data?.message || '匯入正在執行中，請稍候',
          key: 'execute',
          duration: 5,
        });
        return;
      }
      message.error({
        content: '執行匯入失敗：' + (error.message || '未知錯誤'),
        key: 'execute',
        duration: 5,
      });
      setContactProgressByScheduleId((prev) => {
        const n = { ...prev };
        delete n[schedule.id];
        return n;
      });
    } finally {
      if (!keepRunning) {
        setRunningContactScheduleIds((a) => a.filter((x) => x !== schedule.id));
      }
    }
  };

  // 更新排程狀態
  const handleToggleActive = async (schedule) => {
    try {
      // 使用 camelCase（後端已配置自動映射到 PascalCase）
      await contactImportApi.updateScheduleStatus(schedule.id, {
        isActive: !schedule.isActive
      });
      message.success(schedule.isActive ? '排程已停用' : '排程已啟用');
      loadSchedules();
    } catch (error) {
      console.error('❌ 更新排程狀態失敗:', error);
      console.error('❌ 錯誤詳情:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      const errorMessage = error.response?.data?.message || error.message || '更新排程狀態失敗';
      message.error('更新排程狀態失敗：' + errorMessage);
    }
  };

  // 更新排程
  const handleUpdateSchedule = async () => {
    try {
      // 確保 sourceConfig 和 fieldMapping 是對象，不是字符串
      let sourceConfig = formData.sourceConfig || {};
      const importType = formData.importType || editingSchedule?.importType;
      if (!isTenantAdmin && importType === 'sql' && editingSchedule?.sourceConfig) {
        try {
          sourceConfig = typeof editingSchedule.sourceConfig === 'string'
            ? JSON.parse(editingSchedule.sourceConfig)
            : editingSchedule.sourceConfig;
        } catch (e) {
          console.error('解析原始 SQL sourceConfig 失敗:', e);
        }
      }
      let fieldMapping = formData.fieldMapping || {};
      
      // 如果它們是字符串，嘗試解析
      if (typeof sourceConfig === 'string') {
        try {
          sourceConfig = JSON.parse(sourceConfig);
        } catch (e) {
          console.error('解析 sourceConfig 失敗:', e);
          sourceConfig = {};
        }
      }
      
      // 從字段映射表單獲取最新的映射值
      let broadcastGroupId = null;
      try {
        const mappingValues = fieldMappingForm.getFieldsValue();
        if (mappingValues && Object.keys(mappingValues).length > 0) {
          // 從 fieldMapping 中分離出 broadcastGroupId
          broadcastGroupId = (mappingValues.broadcastGroupId && mappingValues.broadcastGroupId !== '') 
            ? mappingValues.broadcastGroupId 
            : null;
          
          // 創建不包含 broadcastGroupId 的 fieldMapping
          const { broadcastGroupId: _, ...fieldMappingWithoutBroadcastGroup } = mappingValues;
          fieldMapping = fieldMappingWithoutBroadcastGroup;
          
          // 處理 hashtags：如果是數組，轉換為字符串（取第一個值，因為 fieldMapping 只需要一個列名）
          if (fieldMapping.hashtags !== undefined && fieldMapping.hashtags !== null) {
            if (Array.isArray(fieldMapping.hashtags)) {
              fieldMapping.hashtags = fieldMapping.hashtags.length > 0 ? fieldMapping.hashtags[0] : null;
            }
            // 如果轉換後是 null 或空字符串，移除該字段
            if (!fieldMapping.hashtags) {
              delete fieldMapping.hashtags;
            }
          }
        }
      } catch (e) {
        console.warn('獲取字段映射表單值失敗，使用原有值:', e);
      }
      
      if (typeof fieldMapping === 'string') {
        try {
          fieldMapping = JSON.parse(fieldMapping);
          // 如果解析後是字符串，再次檢查並移除 broadcastGroupId
          if (fieldMapping && typeof fieldMapping === 'object' && 'broadcastGroupId' in fieldMapping) {
            broadcastGroupId = fieldMapping.broadcastGroupId || broadcastGroupId;
            const { broadcastGroupId: _, ...fieldMappingWithoutBroadcastGroup } = fieldMapping;
            fieldMapping = fieldMappingWithoutBroadcastGroup;
          }
          // 處理 hashtags：如果是數組，轉換為字符串
          if (fieldMapping && typeof fieldMapping === 'object' && fieldMapping.hashtags !== undefined && fieldMapping.hashtags !== null) {
            if (Array.isArray(fieldMapping.hashtags)) {
              fieldMapping.hashtags = fieldMapping.hashtags.length > 0 ? fieldMapping.hashtags[0] : null;
            }
            // 如果轉換後是 null 或空字符串，移除該字段
            if (!fieldMapping.hashtags) {
              delete fieldMapping.hashtags;
            }
          }
        } catch (e) {
          console.error('解析 fieldMapping 失敗:', e);
          fieldMapping = {};
        }
      }
      
      // 如果 fieldMapping 是對象，確保移除 broadcastGroupId 並處理 hashtags
      if (fieldMapping && typeof fieldMapping === 'object') {
        if ('broadcastGroupId' in fieldMapping) {
          broadcastGroupId = fieldMapping.broadcastGroupId || broadcastGroupId;
          const { broadcastGroupId: _, ...fieldMappingWithoutBroadcastGroup } = fieldMapping;
          fieldMapping = fieldMappingWithoutBroadcastGroup;
        }
        // 處理 hashtags：如果是數組，轉換為字符串
        if (fieldMapping.hashtags !== undefined && fieldMapping.hashtags !== null) {
          if (Array.isArray(fieldMapping.hashtags)) {
            fieldMapping.hashtags = fieldMapping.hashtags.length > 0 ? fieldMapping.hashtags[0] : null;
          }
          // 如果轉換後是 null 或空字符串，移除該字段
          if (!fieldMapping.hashtags) {
            delete fieldMapping.hashtags;
          }
        }
      }
      
      // 確保 intervalMinutes 是數字
      const intervalMinutes = formData.scheduleType === 'interval' 
        ? (formData.intervalMinutes || 60) 
        : null;
      
      // 使用 camelCase（後端已配置自動映射到 PascalCase）
      const updateData = {
        name: formData.name || '',
        isScheduled: formData.isScheduled !== undefined ? formData.isScheduled : true,
        scheduleType: formData.scheduleType || 'interval',
        intervalMinutes: intervalMinutes,
        scheduleCron: null, // 如果後續需要支持 cron，可以添加
        sourceConfig: sourceConfig,
        fieldMapping: fieldMapping,
        allowUpdateDuplicates: editingSchedule?.allowUpdateDuplicates || false,
        broadcastGroupId: (broadcastGroupId && broadcastGroupId !== '') ? broadcastGroupId : (editingSchedule?.broadcastGroupId || null)
      };
      
      console.log('📤 發送更新排程數據:', updateData);
      console.log('📤 sourceConfig:', JSON.stringify(sourceConfig, null, 2));
      console.log('📤 fieldMapping:', JSON.stringify(fieldMapping, null, 2));
      
      await contactImportApi.updateSchedule(editingSchedule.id, updateData);
      message.success('排程已更新');
      setEditModalVisible(false);
      loadSchedules();
    } catch (error) {
      console.error('❌ 更新排程失敗:', error);
      console.error('❌ 錯誤詳情:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      const errorMessage = error.response?.data?.message || error.message || '更新排程失敗';
      message.error('更新排程失敗：' + errorMessage);
    }
  };

  // 編輯排程
  const handleEdit = async (schedule) => {
    setEditingSchedule(schedule);
    
    // 解析 sourceConfig 和 fieldMapping
    let sourceConfig = {};
    let fieldMapping = {};
    
    console.log('📋 編輯排程:', schedule);
    console.log('📋 sourceConfig:', schedule.sourceConfig);
    console.log('📋 fieldMapping:', schedule.fieldMapping);
    
    try {
      // schedule.sourceConfig 可能是字符串或對象
      if (schedule.sourceConfig) {
        if (typeof schedule.sourceConfig === 'string') {
          sourceConfig = JSON.parse(schedule.sourceConfig);
        } else {
          sourceConfig = schedule.sourceConfig;
        }
        console.log('✅ 解析後的 sourceConfig:', sourceConfig);
      }
      if (schedule.fieldMapping) {
        if (typeof schedule.fieldMapping === 'string') {
          fieldMapping = JSON.parse(schedule.fieldMapping);
        } else {
          fieldMapping = schedule.fieldMapping;
        }
        console.log('✅ 解析後的 fieldMapping:', fieldMapping);
      }
    } catch (e) {
      console.error('❌ 解析配置失敗:', e);
    }
    
    setFormData({
      name: schedule.name,
      isScheduled: schedule.isScheduled,
      scheduleType: schedule.scheduleType || 'interval',
      intervalMinutes: schedule.intervalMinutes || 60,
      status: schedule.status,
      isActive: schedule.isActive,
      importType: schedule.importType,
      sourceConfig: sourceConfig,
      fieldMapping: fieldMapping
    });
    
    // 設置字段映射表單值（包括 broadcastGroupId）
    const formValues = { ...fieldMapping };
    // 將 broadcastGroupId 添加到表單值中
    if (schedule.broadcastGroupId) {
      formValues.broadcastGroupId = schedule.broadcastGroupId;
    }
    fieldMappingForm.setFieldsValue(formValues);
    
    console.log('📋 設置表單值:', formValues);
    console.log('📋 schedule.broadcastGroupId:', schedule.broadcastGroupId);
    
    // 打開編輯 modal（useEffect 會自動載入預覽數據）
    setEditModalVisible(true);
  };

  // 獲取狀態標籤
  const getStatusTag = (status, isActive) => {
    if (!isActive) {
      return <Tag color="default">{t('common.inactive')}</Tag>;
    }
    switch (status) {
      case 'Active':
        return <Tag color="success">{t('common.active')}</Tag>;
      case 'Paused':
        return <Tag color="warning">{t('common.paused')}</Tag>;
      case 'Inactive':
        return <Tag color="default">{t('common.inactive')}</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  // 獲取匯入類型標籤
  const getImportTypeTag = (type) => {
    const tags = {
      excel: { color: 'green', text: 'Excel' },
      google: { color: 'blue', text: 'Google Sheets' },
      sql: { color: 'purple', text: 'SQL Database' }
    };
    const tag = tags[type] || { color: 'default', text: type };
    return <Tag color={tag.color}>{tag.text}</Tag>;
  };

  const columns = [
    {
      title: t('contactImport.scheduleName'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true
    },
    {
      title: t('contactImport.importType'),
      dataIndex: 'importType',
      key: 'importType',
      width: 120,
      render: (type) => getImportTypeTag(type)
    },
    {
      title: t('contactImport.executionFrequency'),
      key: 'schedule',
      width: 150,
      render: (record) => {
        if (!record.isScheduled) return '-';
        switch (record.scheduleType) {
          case 'interval':
            return `${t('common.every')} ${record.intervalMinutes} ${t('common.minutes')}`;
          case 'daily':
            return t('contactImport.daily');
          case 'weekly':
            return t('contactImport.weekly');
          default:
            return record.scheduleType;
        }
      }
    },
    {
      title: t('contactImport.lastExecution'),
      dataIndex: 'lastRunAt',
      key: 'lastRunAt',
      width: 180,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: t('contactImport.nextExecution'),
      dataIndex: 'nextRunAt',
      key: 'nextRunAt',
      width: 180,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: t('common.status'),
      key: 'status',
      width: 100,
      render: (record) => getStatusTag(record.status, record.isActive)
    },
    {
      title: t('contactImport.importExecutionProgress'),
      key: 'contactImportProgress',
      width: 260,
      render: (record) => {
        const d = contactProgressByScheduleId[record.id];
        if (!d) return <span style={{ fontSize: 12, color: '#999' }}>—</span>;
        const st = d.syncStatus || 'Idle';
        if (st === 'Running') {
          const total = d.totalRecordsToSync || 0;
          const processed = d.recordsProcessed || 0;
          const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
          const ok = Number(d.recordsInserted || 0);
          const fail = Number(d.importFailCount || 0);
          return (
            <div style={{ minWidth: 220 }}>
              <Progress percent={pct} size="small" status="active" />
              <div style={{ fontSize: 11, lineHeight: 1.2, marginTop: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2, color: '#666' }}>
                  <span>{processed.toLocaleString()} / {total.toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px' }}>
                  <span style={{ color: '#52c41a', fontWeight: 500 }}>成功 {ok.toLocaleString()}</span>
                  {fail > 0 ? <span style={{ color: '#ff4d4f', fontWeight: 500 }}>失敗 {fail.toLocaleString()}</span> : null}
                </div>
              </div>
            </div>
          );
        }
        if (st === 'Completed') {
          const ok = Number(d.recordsInserted || 0);
          const fail = Number(d.importFailCount || 0);
          return (
            <div style={{ minWidth: 200 }}>
              <Progress percent={100} size="small" status="success" />
              <div style={{ fontSize: 11, lineHeight: 1.2, marginTop: 4 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px' }}>
                  <span style={{ color: '#52c41a', fontWeight: 500 }}>成功 {ok.toLocaleString()}</span>
                  {fail > 0 ? <span style={{ color: '#ff4d4f', fontWeight: 500 }}>失敗 {fail.toLocaleString()}</span> : null}
                </div>
              </div>
            </div>
          );
        }
        if (st === 'Failed') {
          return (
            <div style={{ minWidth: 200 }}>
              <Progress percent={0} size="small" status="exception" />
              {d.syncErrorMessage ? (
                <Tooltip title={d.syncErrorMessage}>
                  <div style={{ fontSize: 11, color: '#ff4d4f', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.syncErrorMessage.length > 40 ? `${d.syncErrorMessage.slice(0, 40)}…` : d.syncErrorMessage}
                  </div>
                </Tooltip>
              ) : null}
            </div>
          );
        }
        return <span style={{ fontSize: 12, color: '#999' }}>—</span>;
      },
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (record) => (
        <Space>
          <Tooltip title={t('contactImport.manualExecute')}>
            <Button 
              type="link" 
              icon={<PlayCircleOutlined />}
              onClick={() => handleExecute(record)}
            />
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.isActive ? t('common.deactivate') : t('common.activate')}>
            <Button 
              type="link" 
              icon={record.isActive ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={() => handleToggleActive(record)}
            />
          </Tooltip>
          <Popconfirm
            title={t('contactImport.confirmDeleteSchedule')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.confirm')}
            cancelText={t('common.cancel')}
          >
            <Tooltip title={t('common.delete')}>
              <Button 
                type="link" 
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 頁面標題 */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Button 
            type="primary"
            shape="square"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/contacts')}
            style={{ 
              width: '40px', 
              height: '40px',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '16px'
            }}
          />
        </Col>
        <Col flex="auto">
          <Title level={2} style={{ margin: 0 }}>
            {t('contactImport.scheduleManagement')}
          </Title>
        </Col>
      </Row>

      {/* 排程列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={schedules}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 筆排程`
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 編輯 Modal */}
      <Modal
        title={t('contactImport.editSchedule')}
        open={editModalVisible}
        onOk={handleUpdateSchedule}
        onCancel={() => {
          setEditModalVisible(false);
          setPreviewData([]);
          setPreviewColumns([]);
          fieldMappingForm.resetFields();
          previewDataLoadedRef.current = false;
          setActiveTabKey('basic');
        }}
        width="95vw"
        style={{ top: 20 }}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        styles={{ 
          body: { 
            height: 'calc(100vh - 180px)', 
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          } 
        }}
      >
        {editingSchedule && (
          <>
            <style>{`
              .edit-schedule-tabs .ant-tabs-content-holder {
                flex: 1;
                display: flex;
                flex-direction: column;
                overflow: hidden;
              }
              .edit-schedule-tabs .ant-tabs-content {
                flex: 1;
                display: flex;
                flex-direction: column;
                overflow: hidden;
              }
              .edit-schedule-tabs .ant-tabs-tabpane {
                flex: 1;
                display: flex;
                flex-direction: column;
                overflow: hidden;
              }
              .edit-schedule-tabs .ant-tabs-tabpane-hidden {
                display: none !important;
              }
            `}</style>
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Tabs
                className="edit-schedule-tabs"
                activeKey={activeTabKey}
                onChange={setActiveTabKey}
                destroyInactiveTabPane={true}
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                items={[
              {
                key: 'basic',
                label: (
                  <span>
                    <SettingOutlined />
                    {t('contactImport.basicSettings')}
                  </span>
                ),
                children: (
                  <div style={{ height: '100%', overflowY: 'auto', paddingRight: 8, flex: 1 }}>
                    <Row gutter={16}>
                      <Col xs={24} lg={12}>
                        <Form layout="vertical">
                          <Form.Item label={t('contactImport.scheduleName')}>
                            <Input
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              placeholder={t('contactImport.scheduleNamePlaceholder')}
                            />
                          </Form.Item>
                          <Form.Item label={t('contactImport.dataSourceType')}>
                            {getImportTypeTag(editingSchedule.importType)}
                          </Form.Item>
                          <Form.Item label={t('commerceImport.scheduleFrequency')}>
                            <Radio.Group
                              value={formData.scheduleType}
                              onChange={(e) => setFormData({ ...formData, scheduleType: e.target.value })}
                            >
                              <Radio value="interval">{t('contactImport.everyXMinutes')}</Radio>
                              <Radio value="daily">{t('contactImport.daily')}</Radio>
                              <Radio value="weekly">{t('contactImport.weekly')}</Radio>
                            </Radio.Group>
                          </Form.Item>
                          {formData.scheduleType === 'interval' && (
                            <Form.Item label={t('commerceImport.scheduleIntervalLabel')}>
                              <InputNumber
                                min={1}
                                max={525600}
                                value={formData.intervalMinutes}
                                onChange={(value) => setFormData({ ...formData, intervalMinutes: value })}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          )}
                          <Form.Item label={t('commerceImport.scheduleEnable')}>
                            <Switch
                              checked={formData.isActive}
                              onChange={(checked) => setFormData({ ...formData, isActive: checked })}
                            />
                          </Form.Item>
                        </Form>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Card title={t('commerceImport.scheduleSourceConfig')} size="small">
                          {formData.importType === 'excel' && formData.sourceConfig && (
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                              <div>
                                <Text strong>{t('contactImport.excelFilePath')}：</Text>
                                <Input
                                  value={formData.sourceConfig.filePath || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sourceConfig: { ...formData.sourceConfig, filePath: e.target.value },
                                  })}
                                  placeholder="/Uploads/excel/example.xlsx"
                                  style={{ marginTop: 8 }}
                                />
                              </div>
                              <div>
                                <Text strong>{t('contactImport.sheetName')}：</Text>
                                <Input
                                  value={formData.sourceConfig.sheetName || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sourceConfig: { ...formData.sourceConfig, sheetName: e.target.value },
                                  })}
                                  placeholder="Sheet1"
                                  style={{ width: '100%', marginTop: 8 }}
                                />
                              </div>
                            </Space>
                          )}
                          {formData.importType === 'google' && formData.sourceConfig && (
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                              <div>
                                <Text strong>{t('contactImport.googleDocsUrl')}：</Text>
                                <Input
                                  value={formData.sourceConfig.url || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sourceConfig: { ...formData.sourceConfig, url: e.target.value },
                                  })}
                                  placeholder="https://docs.google.com/spreadsheets/d/..."
                                  style={{ marginTop: 8 }}
                                />
                              </div>
                              {formData.sourceConfig.fileType === 'googlesheets' && (
                                <div>
                                  <Text strong>{t('contactImport.sheetName')}：</Text>
                                  <Input
                                    value={formData.sourceConfig.sheetName || ''}
                                    onChange={(e) => setFormData({
                                      ...formData,
                                      sourceConfig: { ...formData.sourceConfig, sheetName: e.target.value },
                                    })}
                                    placeholder="Sheet1"
                                    style={{ width: '100%', marginTop: 8 }}
                                  />
                                </div>
                              )}
                            </Space>
                          )}
                          {formData.importType === 'sql' && formData.sourceConfig && (
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                              {!isTenantAdmin && (
                                <Alert
                                  message={t('dataSetManagement.sqlReadOnlyNotice', 'SQL 查詢由 Tenant_Admin 管理，您只能查看，無法修改。')}
                                  type="warning"
                                  showIcon
                                />
                              )}
                              <div>
                                <Text strong>{t('contactImport.server')}：</Text>
                                <Input
                                  value={formData.sourceConfig.server || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sourceConfig: { ...formData.sourceConfig, server: e.target.value },
                                  })}
                                  placeholder="localhost"
                                  style={{ width: '100%', marginTop: 8 }}
                                  disabled={!isTenantAdmin}
                                />
                              </div>
                              <div>
                                <Text strong>{t('contactImport.database')}：</Text>
                                <Input
                                  value={formData.sourceConfig.database || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sourceConfig: { ...formData.sourceConfig, database: e.target.value },
                                  })}
                                  placeholder="contacts_db"
                                  style={{ width: '100%', marginTop: 8 }}
                                  disabled={!isTenantAdmin}
                                />
                              </div>
                              <div>
                                <Text strong>{t('contactImport.username')}：</Text>
                                <Input
                                  value={formData.sourceConfig.username || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sourceConfig: { ...formData.sourceConfig, username: e.target.value },
                                  })}
                                  placeholder="username"
                                  style={{ width: '100%', marginTop: 8 }}
                                  disabled={!isTenantAdmin}
                                />
                              </div>
                              <div>
                                <Text strong>{t('contactImport.password')}：</Text>
                                <Input.Password
                                  value={formData.sourceConfig.password || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sourceConfig: { ...formData.sourceConfig, password: e.target.value },
                                  })}
                                  placeholder="password"
                                  style={{ width: '100%', marginTop: 8 }}
                                  disabled={!isTenantAdmin}
                                />
                              </div>
                              <div>
                                <Text strong>{t('contactImport.tableName')}：</Text>
                                <Input
                                  value={formData.sourceConfig.table || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sourceConfig: { ...formData.sourceConfig, table: e.target.value },
                                  })}
                                  placeholder="contacts"
                                  style={{ width: '100%', marginTop: 8 }}
                                  disabled={!isTenantAdmin}
                                />
                              </div>
                              <div>
                                <Text strong>{t('contactImport.customQuery')}：</Text>
                                <TextArea
                                  value={formData.sourceConfig.query || ''}
                                  onChange={(e) => setFormData({
                                    ...formData,
                                    sourceConfig: { ...formData.sourceConfig, query: e.target.value },
                                  })}
                                  placeholder="SELECT * FROM contacts WHERE..."
                                  rows={3}
                                  style={{ width: '100%', marginTop: 8 }}
                                  disabled={!isTenantAdmin}
                                />
                              </div>
                              {isTenantAdmin && (
                                <Space wrap>
                                  <Button onClick={handleTestSql}>{t('contactImport.testConnection')}</Button>
                                  <Button type="primary" onClick={handleReloadPreview} loading={loadingPreview}>
                                    {t('commerceImport.reloadPreview')}
                                  </Button>
                                </Space>
                              )}
                            </Space>
                          )}
                          {(formData.importType === 'excel' || formData.importType === 'google') && (
                            <div style={{ marginTop: 12 }}>
                              <Button type="primary" onClick={handleReloadPreview} loading={loadingPreview}>
                                {t('commerceImport.reloadPreview')}
                              </Button>
                            </div>
                          )}
                        </Card>
                        {editingSchedule?.lastRunAt && (
                          <div style={{ marginTop: 12 }}>
                            <Text type="secondary">
                              {t('contactImport.lastExecution')}：{dayjs(editingSchedule.lastRunAt).format('YYYY-MM-DD HH:mm:ss')}
                            </Text>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>
                )
              },
              {
                key: 'fieldMapping',
                label: (
                  <span>
                    <UnorderedListOutlined />
                    {t('contactImport.fieldMapping')}
                  </span>
                ),
                children: (
                  <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Spin spinning={loadingPreview} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                      <Row gutter={16} style={{ height: '100%', flex: 1, display: 'flex', margin: 0, minHeight: 0 }}>
                        <Col span={12} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, paddingRight: '8px' }}>
                        <Card 
                          title={t('contactImport.dataPreview')}
                          style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
                          styles={{ body: { flex: 1, padding: '16px', overflow: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 } }}
                        >
                          {loadingPreview ? (
                            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                              <Spin size="large" />
                              <div style={{ marginTop: '16px', color: '#999' }}>
                                {t('contactImport.loadingPreview')}
                              </div>
                            </div>
                          ) : (
                            <>
                          {(formData.importType || editingSchedule?.importType) === 'sql' && sqlPreviewMeta && (
                            <div style={{ padding: '0 0 12px' }}>
                              {sqlPreviewMeta.totalMatchingRows != null && (
                                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                                  {t('contactImport.sqlPreviewTotalRows').replace('{count}', String(sqlPreviewMeta.totalMatchingRows))}
                                </Text>
                              )}
                              {sqlPreviewMeta.previewTopApplied != null && (
                                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                                  {t('contactImport.sqlPreviewTopNote').replace('{n}', String(sqlPreviewMeta.previewTopApplied))}
                                </Text>
                              )}
                              {sqlPreviewMeta.previewTopSkippedForWithCte && (
                                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                                  {t('contactImport.sqlPreviewWithCteNote')}
                                </Text>
                              )}
                            </div>
                          )}
                          {previewColumns.length > 0 && previewData.length > 0 ? (
                            <Table
                              columns={previewColumns.map(col => ({
                                ...col,
                                ellipsis: true,
                                resizable: true,
                                width: 120,
                                title: (
                                  <div style={{ 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap',
                                    maxWidth: '120px'
                                  }}>
                                    {col.title || col.dataIndex}
                                  </div>
                                )
                              }))}
                              dataSource={previewData}
                              pagination={{ 
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 筆，共 ${total} 筆`
                              }}
                              size="small"
                              scroll={{ x: 'max-content', y: '100%' }}
                              bordered
                              components={{
                                header: {
                                  cell: (props) => (
                                    <th 
                                      {...props} 
                                      style={{ 
                                        ...props.style, 
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        minWidth: '120px',
                                        maxWidth: '200px',
                                        resize: 'horizontal',
                                        cursor: 'col-resize'
                                      }} 
                                    />
                                  )
                                }
                              }}
                            />
                          ) : (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                              {t('contactImport.noPreviewData')}
                            </div>
                          )}
                            </>
                          )}
                        </Card>
                        </Col>
                      <Col span={12} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, paddingLeft: '8px' }}>
                        <Card 
                          title={t('contactImport.fieldMapping')}
                          style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
                          styles={{ body: { flex: 1, padding: '16px', overflow: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 } }}
                        >
                          <ContactImportFieldMapping
                            form={fieldMappingForm}
                            columns={previewColumns}
                            groups={groups}
                            hashtags={hashtags}
                          />
                        </Card>
                        </Col>
                      </Row>
                    </Spin>
                  </div>
                )
              }
            ]}
            />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ContactImportSchedulePage;

